import { useMemo } from "react";
import type { ComponentParameter, UnmappedElement } from "../types";
import { getParamOptionValue } from "../utils/paramOptions";

/** How sibling components on the same pay element are shown in Map Columns. */
export type MappingLayoutMode = "grouped" | "individual";

export interface MappingElements {
  elements: UnmappedElement[];
  /** Populated in grouped mode: all component names per pay element. */
  componentNamesByElement: Record<string, string[]>;
  unknownComponents: string[];
  /** True when at least one pay element has multiple discovered components. */
  hasSharedElements: boolean;
}

function buildGroupedElements(
  componentNames: string[],
  paramsByComponent: Record<string, ComponentParameter[]>,
): UnmappedElement[] {
  const paramListsByElement = new Map<string, ComponentParameter[][]>();
  const representativeByElement = new Map<string, string>();
  const elementOrder: string[] = [];

  for (const name of componentNames) {
    const params = paramsByComponent[name] ?? [];
    if (params.length === 0) continue;
    const el = params[0].elementQualifiedName;
    if (!paramListsByElement.has(el)) {
      paramListsByElement.set(el, []);
      representativeByElement.set(el, name);
      elementOrder.push(el);
    }
    paramListsByElement.get(el)!.push(params);
  }

  return elementOrder.map((el) => {
    const lists = paramListsByElement.get(el)!;
    const sealedIds = new Set<string>();
    for (const list of lists) {
      for (const p of list) {
        if (p.isSealed) sealedIds.add(getParamOptionValue(p));
      }
    }
    const representativeParams = lists[0].map((p) =>
      !p.isSealed && sealedIds.has(getParamOptionValue(p))
        ? { ...p, isSealed: true }
        : p,
    );
    return {
      elementQualifiedName: el,
      componentCode: representativeByElement.get(el)!,
      parameters: representativeParams,
    };
  });
}

function buildIndividualElements(
  componentNames: string[],
  paramsByComponent: Record<string, ComponentParameter[]>,
): UnmappedElement[] {
  return componentNames
    .map((name) => {
      const params = paramsByComponent[name] ?? [];
      if (params.length === 0) return null;
      return {
        elementQualifiedName: params[0].elementQualifiedName,
        componentCode: name,
        parameters: params,
      };
    })
    .filter((el): el is UnmappedElement => el != null);
}

/** Shared pay elements (2+ components) first; stable order within each bucket. */
function sortElementsSharedFirst(
  elements: UnmappedElement[],
  componentNamesByElement: Record<string, string[]>,
): UnmappedElement[] {
  const isShared = (el: UnmappedElement) =>
    (componentNamesByElement[el.elementQualifiedName]?.length ?? 0) > 1;

  const shared: UnmappedElement[] = [];
  const solo: UnmappedElement[] = [];
  for (const el of elements) {
    (isShared(el) ? shared : solo).push(el);
  }
  return [...shared, ...solo];
}

/**
 * Derive mappable sections for the Map Columns screen.
 *
 * In **grouped** mode, components on the same pay element share one mapping
 * table (element-level). In **individual** mode, each component gets its own
 * table even when siblings share an element.
 */
export function useMappingElements(
  componentNames: string[],
  paramsByComponent: Record<string, ComponentParameter[]>,
  layoutMode: MappingLayoutMode = "grouped",
): MappingElements {
  const componentNamesByElement = useMemo(() => {
    const map: Record<string, string[]> = {};
    for (const name of componentNames) {
      const params = paramsByComponent[name] ?? [];
      if (params.length === 0) continue;
      const el = params[0].elementQualifiedName;
      (map[el] ??= []).push(name);
    }
    return map;
  }, [paramsByComponent, componentNames]);

  const elements = useMemo(() => {
    const built =
      layoutMode === "individual"
        ? buildIndividualElements(componentNames, paramsByComponent)
        : buildGroupedElements(componentNames, paramsByComponent);
    return sortElementsSharedFirst(built, componentNamesByElement);
  }, [layoutMode, paramsByComponent, componentNames, componentNamesByElement]);

  const unknownComponents = useMemo(
    () => componentNames.filter((name) => (paramsByComponent[name] ?? []).length === 0),
    [paramsByComponent, componentNames],
  );

  const hasSharedElements = useMemo(
    () => Object.values(componentNamesByElement).some((names) => names.length > 1),
    [componentNamesByElement],
  );

  return { elements, componentNamesByElement, unknownComponents, hasSharedElements };
}

/** Stable key for form state / selections in the active layout mode. */
export function mappingSelectionKey(
  element: UnmappedElement,
  layoutMode: MappingLayoutMode,
): string {
  return layoutMode === "grouped"
    ? element.elementQualifiedName
    : element.componentCode;
}
